// Learner-facing copy for the docs/09 error catalog — kept in step with the
// SCO's cheat sheet (rapid-api-lms/scorm/template/poll-client.js).
export const errorCopy = code => {
  switch (code) {
    case 'TOKEN_EXPIRED':
    case 'LAUNCH_TOKEN_EXPIRED':
      return {
        title: 'This launcher has expired',
        explanation: 'Launchers stay valid for 4 hours after you click the course.',
        action: 'Return to your LMS and click the course again.'
      }
    case 'LAUNCH_TOKEN_FAILED':
      return {
        title: 'Session ended unexpectedly',
        explanation: "Your previous session didn't complete successfully.",
        action: 'Return to your LMS and start the course again.'
      }
    case 'LAUNCH_TOKEN_REJECTED':
      return {
        title: 'This session was rejected',
        explanation: 'The launcher could not be verified.',
        action: 'Return to your LMS and click the course again. If it persists, contact your training admin.'
      }
    case 'LAUNCH_TOKEN_NOT_FOUND':
      return {
        title: "We couldn't find this session",
        explanation: 'The link may be incomplete or out of date.',
        action: 'Return to your LMS and click the course again.'
      }
    case 'LINK_USER_ARCHIVED':
      return {
        title: 'This EducationXR account is no longer active',
        explanation: 'Your account has been removed or archived.',
        action: 'Contact your training admin, or sign in with a different account.'
      }
    // /lms/identity refusals (plan Task 14B). Retrying cannot help until an
    // admin acts, so neither offers "try again": the action is the whole answer.
    case 'ACCESS_ENDED':
      return {
        title: 'Your access to this organization has ended',
        explanation: 'Your membership was removed, so this course cannot link your account.',
        action: 'Ask its admin.'
      }
    case 'SEAT_LIMIT_REACHED':
      return {
        title: 'This organization has no free seats',
        explanation: 'Every seat on its plan is taken, so this course cannot link a new account.',
        action: 'Ask its admin.'
      }
    case 'RATE_LIMITED':
      return {
        title: 'Too many attempts',
        explanation: 'Please wait a moment before trying again.',
        action: 'Wait a few seconds, then try again.'
      }
    // LMS final review M7: the codes that still read "Something went wrong".
    case 'UNAUTHORIZED':
      return {
        title: 'Your sign-in has expired',
        explanation: 'For your security, you were signed out.',
        action: 'Sign in again, then return to this page from your course.'
      }
    case 'STATE_CONFLICT':
      return {
        title: 'This launch has already moved on',
        explanation: 'It was linked or started in another window or on another device.',
        action: 'Return to your course tab, or click the course in your LMS again.'
      }
    // The launch gate (I1): an admin turned the connection, the organization's
    // LMS launches or the course off. Retrying cannot help until they act.
    case 'CONNECTION_PAUSED':
    case 'CONNECTION_ARCHIVED':
      return {
        title: "This LMS connection isn't active",
        explanation: 'Your admin has paused or removed this LMS connection.',
        action: 'Contact your training admin.'
      }
    case 'FEATURE_DISABLED':
      return {
        title: "LMS launches aren't available right now",
        explanation: 'LMS launches are turned off for your organization.',
        action: 'Contact your training admin.'
      }
    case 'COURSE_NOT_AVAILABLE':
      return {
        title: "This course isn't available",
        explanation: "It is no longer connected to your LMS, or isn't open to your account.",
        action: 'Contact your training admin.'
      }
    // Phase F: the launch window's mint (auth, F-4) refuses every launch it
    // will not make with this one answer, so it says where to go and not why.
    case 'lms_launch.unavailable':
      return {
        title: "This launch isn't available",
        explanation: "This course can't be opened from here right now.",
        action: 'Open the course from your LMS again.'
      }
    // The mint's one distinct refusal (auth, HTTP 403): this LMS account is
    // linked to another EducationXR account. It names no one; the window
    // offers "Sign in with a different account".
    case 'lms_launch.linked_to_other_account':
      return {
        title: 'This LMS account is linked to another EducationXR account',
        explanation: 'Sign in with the account you used before, or ask your training admin to reset the link.',
        action: 'Sign in with a different account.'
      }
    // The identity step links an account only for a person's own full sign-in
    // (lms D-107): a PIN session or a token with no session id, and an
    // impersonated session, are refused. The way forward is a normal sign-in.
    case 'FULL_SESSION_REQUIRED':
      return {
        title: 'Sign in with your own account',
        explanation: "This sign-in (a PIN or a limited session) can't connect an LMS account.",
        action: 'Sign in with your own EducationXR account, not a PIN.'
      }
    case 'IMPERSONATION_NOT_ALLOWED':
      return {
        title: 'Sign in with your own account',
        explanation: "You are signed in as someone else, which can't connect an LMS account.",
        action: 'Sign in with your own EducationXR account.'
      }
    // A connection whose link policy is sso_only (lms D-113) links only a
    // session that signed in through the organization's single sign-on. The
    // window's button starts that sign-in (layout-launch.js); nothing here
    // signs anyone out, and retrying the same session cannot help.
    case 'LINK_SSO_REQUIRED':
      return {
        title: 'Sign in through your organization',
        explanation: "Your organization connects LMS accounts only when you sign in with its single sign-on, and this sign-in didn't.",
        action: "Sign in with your organization's SSO to connect your account."
      }
    case 'LINK_ALREADY_EXISTS':
      return {
        title: 'This LMS account is already connected',
        explanation: 'It is connected to a different EducationXR account.',
        action: "Sign in with that account, or ask your organization's admin."
      }
    default:
      return {
        title: 'Something went wrong',
        explanation: 'An unexpected error occurred.',
        action: 'Try again. If it persists, contact your training admin.'
      }
  }
}
